import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Brush, Tooltip, Legend, ResponsiveContainer, Label, ReferenceLine, CartesianGrid, Cell } from 'recharts';
import { convertSingleValue, japaneseTickFormatter } from '../utils/commonUtils';
import Loading from '../Loading';

const range = (y) => {
    const yMax = Math.max(...y);
    const yMin = Math.min(...y);
    const range = yMax - yMin;

    const roundToOneDecimal = (value) => parseFloat(value.toFixed(1));

    if (yMin > 0) {
        const rangeMax = roundToOneDecimal(yMax * 1.1);
        const rangeMin = 0;
        return [rangeMin, rangeMax];
    } else if (yMax < 0) {
        const rangeMax = 0;
        const rangeMin = roundToOneDecimal(yMin * 1.1);
        return [rangeMin, rangeMax];
    } else {
        const rangeMax = roundToOneDecimal(yMax + range * 0.1);
        const rangeMin = roundToOneDecimal(yMin - range * 0.1);
        return [rangeMin, rangeMax];
    }
};

const SummaryBarGraph = ({ data, unit, height = 500, width, showLabel}) => {
    const [isSmallArea, setIsSmallArea] = useState(false);
    const [activeIndex, setActiveIndex] = useState(null);

    const handleMouseEnter = (_, index) => {
        setActiveIndex(index)
    }

    const handleMouseLeave = () => {
        setActiveIndex(null)
    }

    useEffect(() => {
        if (data && width / data.length < 80 ) {
            setIsSmallArea(true);
        } else {
            setIsSmallArea(false);
        }
    }, [width, data])

    const stopColor = {
        start: "rgba(45, 186, 177, .5)", 
        end: "rgba(45, 186, 77, .5)"
    };

    const stopColorHover = {
        start: "rgba(45, 186, 177, .8)", 
        end: "rgba(45, 186, 77, .8)"
    };

    const CustomLabel = ({ x, y, width, height, value, index }) => {
        const offset = value >= 0 ? -10 : 20; // 正の値と負の値でラベルの位置を調整
        const centerX = x + width / 2; // バーの中心
        const centerY = y + offset; // 上か下かで調整
        return (
            <text
                x={centerX}
                y={centerY}
                fill="black"
                textAnchor="middle"
                fontSize={14}
            >
                {convertSingleValue(value) + unit}
            </text>
        );
    };
    
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-tooltip text-white px-2 py-1 border border-gray-300 bg-gray-600 rounded shadow">
                    {payload.map((item, index) => {
                        return (
                            <div key={index}>
                                {`${item.payload.x}: ${convertSingleValue(item.payload[item['dataKey']])}` + unit}
                            </div>
                        );
                    })}

                </div>
            );
        }
        return null;
    };
    
    const uniqueId = React.useMemo(() => `gradient-${Math.random().toString(36).substr(2, 9)}`, []);
    if (!data) {
        return <Loading />
    }
    
    if (data.length === 0) {
        return <div className='w-full h-[400px] align-center content-center text-lg'>データなし</div>
    }
    
    return (
        <ResponsiveContainer width="100%" height={height}>
            <BarChart
                data={data}
                margin={{ top: 20, right: 5, left: ((showLabel === 'auto' && isSmallArea) || showLabel==='hidden') ? 5: 5, bottom: 40 }}
                barCategoryGap="20%"
                barGap={5}
            >
                <defs>
                    <linearGradient id={`${uniqueId}-0`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={stopColorHover.start} />
                        <stop offset="95%" stopColor={stopColor.end} />
                    </linearGradient>
                    <linearGradient id={`${uniqueId}-1`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={stopColorHover.start} />
                        <stop offset="95%" stopColor={stopColorHover.end} />
                    </linearGradient>
                </defs>
                {((showLabel === 'auto' && isSmallArea) || showLabel==='hidden') && <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />}
                <XAxis
                    dataKey="x"
                    tick={{ fontSize: 12, fill: '#000' }}>
                </XAxis>
                {((showLabel === 'auto' && isSmallArea) || showLabel==='hidden') &&
                    <YAxis tickFormatter={(value) => japaneseTickFormatter(value, unit)}
                        domain={range(data.map((dt) => dt.y))}
                        tickCount={4}
                     >
                        <Label
                            position="top"
                            offset={20}
                            style={{ textAnchor: 'middle', fill: 'grey' }}
                        />
                    </YAxis>
                }
                <ReferenceLine y={0} stroke="rgb(100, 100, 100)" />
                <Tooltip
                    content={<CustomTooltip />}
                    cursor={false}
                />
                <Bar
                    dataKey="y"
                    fill={`url(#${uniqueId}-0)`}
                    label={((!isSmallArea && showLabel==='auto') || showLabel==='visible') ? <CustomLabel unit={unit} /> : null}
                    radius={isSmallArea ? [3, 3, 0, 0] : [5, 5, 0, 0]}
                    animationBegin={0}
                    animationDuration={1000}
                    animationEasing="ease-in-out"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    {data.map((_, index) => (
                        <Cell
                        key={`cell-${index}`}
                        opacity={activeIndex === null || index === activeIndex ? 1 : 0.3}
                        />
                    
                ))}
                </Bar>
                <Brush dataKey="x" height={15} stroke="lightgrey" />
            </BarChart>
        </ResponsiveContainer>
    );
};

export default SummaryBarGraph;
